/**
 * Pure builder that produces the Ant `MenuProps['items']` list for a single
 * tree row's more-actions menu.
 *
 * Follows the CLAUDE.md "disable, don't hide" rule for state-driven gating:
 * - When the tree already has a winning combination and *this* node is the
 *   winning one, the menu collapses to "Unmark", the Notes item, and any
 *   pre-built send items. Notes stay reachable so reviewers can annotate the
 *   selected combination without unmarking it.
 * - For every other row, add-child / add-sibling items are emitted per
 *   `entityConfig.allowedChildren` / `allowedSiblings`, skipping any allowed
 *   id with no `entityConfig` entry (configuration, not state) and skipping
 *   add-child past `maxDepth`. When the first allowed child itself has a
 *   first allowed child, one add-branch item adds that whole empty chain of
 *   first allowed children (GC gets EC → DIST → POC, EC gets DIST → POC),
 *   cut at `maxDepth`; its key is not `add-child:`-prefixed, so the
 *   keyboard's add-child shortcuts keep their targets. A Duplicate item is
 *   always emitted: it copies this node and its whole subtree as a following
 *   sibling, disabled only when
 *   the tree is locked by a peer winning branch. The Mark/Unmark winning item
 *   is always emitted
 *   when the entity capability allows it (`supportsWinning` is a config flag,
 *   not state), with `disabled` flipped on when state prerequisites are not
 *   met (no record selected, path incomplete, a descendant already winning,
 *   or another branch already winning). The notes item is always emitted,
 *   disabled when no record is selected. Delete is always emitted; disabled
 *   when this is the single remaining root (deleting it would leave the field
 *   empty) or when the tree is locked by another winning branch.
 * - When another branch already carries the winning combination, this row's
 *   structural items (add-child, add-sibling, delete, mark) are disabled
 *   rather than the entire dropdown — the menu still opens so reviewers can
 *   see why an action is blocked.
 *
 * Stays pure: callers wire the actual handlers; the builder only assembles
 * items + flags.
 */
import { createElement } from 'react';
import type { MenuProps } from 'antd';
import {
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  MessageOutlined,
  PlusOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type {
  RelationshipTreeConfig,
  RelationshipTreeNode,
} from '#types/System.RelationshipTree.types';
import { MAX_TREE_DEPTH } from '#types';

type BuildNodeMenuArgs = {
  node: RelationshipTreeNode;
  depth: number;
  config: RelationshipTreeConfig;
  treeHasWinningCombination: boolean;
  isWinning: boolean;
  hasRecordSelected: boolean;
  nodeHasWinningChildren: boolean;
  hasCompletePath: boolean;
  isLastRootNode: boolean;
  onAddChild: (entityOrGroupId: string) => void;
  onAddBranch: (entityOrGroupIds: string[]) => void;
  onAddSibling: (entityOrGroupId: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMarkWinning: () => void;
  onUnmarkWinning: () => void;
  onEditNotes: () => void;
  sendItems?: MenuProps['items'];
};

const buildNotesItem = (
  node: RelationshipTreeNode,
  hasRecordSelected: boolean,
  onEditNotes: () => void,
): NonNullable<MenuProps['items']>[number] => {
  const hasNotes =
    !!node.extraInformation?.notes && node.extraInformation.notes.length > 0;
  return {
    key: `edit-info:${node.key}`,
    label: hasNotes ? 'Edit Notes' : 'Add Note',
    icon: createElement(MessageOutlined),
    disabled: !hasRecordSelected,
    onClick: onEditNotes,
  };
};

const firstChildChain = (
  config: RelationshipTreeConfig,
  entityOrGroupId: string | undefined,
  remainingDepth: number,
  chain: string[],
): string[] =>
  !entityOrGroupId ||
  remainingDepth === 0 ||
  chain.includes(entityOrGroupId) ||
  !config.entityConfig[entityOrGroupId]
    ? chain
    : firstChildChain(
        config,
        config.entityConfig[entityOrGroupId]?.allowedChildren?.[0],
        remainingDepth - 1,
        [...chain, entityOrGroupId],
      );

export const buildNodeMenu = ({
  node,
  depth,
  config,
  treeHasWinningCombination,
  isWinning,
  hasRecordSelected,
  nodeHasWinningChildren,
  hasCompletePath,
  isLastRootNode,
  onAddChild,
  onAddBranch,
  onAddSibling,
  onDuplicate,
  onDelete,
  onMarkWinning,
  onUnmarkWinning,
  onEditNotes,
  sendItems,
}: BuildNodeMenuArgs): MenuProps['items'] => {
  const entityConfig = config.entityConfig[node.entityOrGroupId];

  if (treeHasWinningCombination && isWinning) {
    const items: MenuProps['items'] = [
      {
        key: `unmark-winning:${node.key}`,
        label: 'Unmark Winning Combination',
        icon: createElement(CloseCircleOutlined),
        disabled: false,
        onClick: onUnmarkWinning,
      },
      buildNotesItem(node, hasRecordSelected, onEditNotes),
    ];
    if (sendItems && sendItems.length > 0) {
      items.push({ type: 'divider' });
      items.push(...sendItems);
    }
    return items;
  }

  const items: MenuProps['items'] = [];
  const isLockedByPeerWinning = treeHasWinningCombination;

  const maxDepth = Math.min(config.maxDepth, MAX_TREE_DEPTH);
  const atMaxDepth = depth >= maxDepth;
  const allowedChildren = entityConfig?.allowedChildren ?? [];
  if (!atMaxDepth) {
    allowedChildren.forEach((childEntityOrGroupId) => {
      const childConfig = config.entityConfig[childEntityOrGroupId];
      if (!childConfig) return;
      const label = `${childConfig.abbreviation ?? childConfig.label ?? childEntityOrGroupId} (Child)`;
      items.push({
        key: `add-child:${node.key}:${childEntityOrGroupId}`,
        label,
        icon: createElement(PlusOutlined),
        disabled: isLockedByPeerWinning,
        onClick: () => onAddChild(childEntityOrGroupId),
      });
    });
    const branch = firstChildChain(
      config,
      allowedChildren[0],
      maxDepth - depth,
      [],
    );
    if (branch.length > 1) {
      const label = branch
        .map(
          (entityOrGroupId) =>
            config.entityConfig[entityOrGroupId]?.abbreviation ??
            config.entityConfig[entityOrGroupId]?.label ??
            entityOrGroupId,
        )
        .join(' → ');
      items.push({
        key: `add-branch:${node.key}:${branch.join(':')}`,
        label: `${label} (Branch)`,
        icon: createElement(PlusOutlined),
        disabled: isLockedByPeerWinning,
        onClick: () => onAddBranch(branch),
      });
    }
  }

  const allowedSiblings = entityConfig?.allowedSiblings ?? [];
  allowedSiblings.forEach((siblingEntityOrGroupId) => {
    const siblingConfig = config.entityConfig[siblingEntityOrGroupId];
    if (!siblingConfig) return;
    const labelBase = siblingConfig.isOperationType
      ? siblingConfig.label
      : (siblingConfig.abbreviation ?? siblingConfig.label ?? 'Node');
    const label =
      depth === 1 ? `${labelBase} Tree (Sibling)` : `${labelBase} (Sibling)`;
    items.push({
      key: `add-sibling:${node.key}:${siblingEntityOrGroupId}`,
      label,
      icon: createElement(PlusOutlined),
      disabled: isLockedByPeerWinning,
      onClick: () => onAddSibling(siblingEntityOrGroupId),
    });
  });

  items.push({
    key: `duplicate:${node.key}`,
    label: 'Duplicate',
    icon: createElement(CopyOutlined),
    disabled: isLockedByPeerWinning,
    onClick: onDuplicate,
  });

  if (entityConfig?.supportsWinning === true) {
    if (isWinning) {
      items.push({
        key: `unmark-winning:${node.key}`,
        label: 'Unmark Winning Combination',
        icon: createElement(CloseCircleOutlined),
        disabled: false,
        onClick: onUnmarkWinning,
      });
    } else {
      const requiresCompletePath =
        entityConfig.requiresCompletePathForWinning === true;
      const pathSatisfied = requiresCompletePath ? hasCompletePath : true;
      const canMark =
        hasRecordSelected &&
        pathSatisfied &&
        !nodeHasWinningChildren &&
        !isLockedByPeerWinning;
      items.push({
        key: `mark-winning:${node.key}`,
        label: 'Mark as Winning Combination',
        icon: createElement(TrophyOutlined),
        disabled: !canMark,
        onClick: onMarkWinning,
      });
    }
  }

  items.push(buildNotesItem(node, hasRecordSelected, onEditNotes));

  items.push({
    key: `delete:${node.key}`,
    label: 'Delete',
    icon: createElement(DeleteOutlined),
    danger: true,
    disabled: isLastRootNode || isLockedByPeerWinning,
    onClick: onDelete,
  });

  if (sendItems && sendItems.length > 0) {
    items.push({ type: 'divider' });
    items.push(...sendItems);
  }

  return items;
};
